'use client'

import React, { useState } from 'react'
import { Box, Flex, Space, Text, Title } from '@mantine/core'
import { useIsMobile } from '@/lib/mobileDetector'
import TestimonialCard from '@/components/TestimonialCard'
import MobileTestimonialCard from '@/components/MobileTestimonialCard'
import { AnimatePresence } from 'framer-motion'
import { Testimonial } from './Store/StoreTestimonials'
import Link from 'next/link'
import { StoreAnalytics, trackStoreEvent } from '@nile/mantine-theme/analytics'

interface TestimonialCarouselProps {
    testimonials: Testimonial[]
    title?: string
    subtitle?: string
}

const TestimonialCarousel: React.FC<TestimonialCarouselProps> = ({ testimonials, title, subtitle }) => {
    const isBreakpoint = useIsMobile(1280)
    const isMobile = useIsMobile()
    const [activeIndex, setActiveIndex] = useState(0)
    const [direction, setDirection] = useState(1) // 1 for right, -1 for left

    const handlePrev = () => {
        setDirection(-1)
        setActiveIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1))
        trackStoreEvent(StoreAnalytics.TESTIMONIAL, { action: 'prev' })
    }

    const handleNext = () => {
        setDirection(1)
        setActiveIndex((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1))
        trackStoreEvent(StoreAnalytics.TESTIMONIAL, { action: 'next' })
    }

    if (!testimonials || testimonials.length === 0) {
        return null
    }

    const currentTestimonial = testimonials[activeIndex]

    return (
        <Box>
            {(title || subtitle) && (
                <Flex direction="column" align={isBreakpoint ? 'flex-start' : 'center'} mb={isBreakpoint ? 24 : 48}>
                    {title && (
                        <Title
                            order={2}
                            size={isBreakpoint ? 30 : 40}
                            ta={isBreakpoint ? 'left' : 'center'}
                            fw="var(--gm-weight-display-large)"
                            c="var(--gm-foreground-fg-primary)"
                            lh={isBreakpoint ? '120%' : '110%'}
                            styles={{
                                root: {
                                    letterSpacing: isBreakpoint ? '0.3px' : '0.96px',
                                },
                            }}
                        >
                            {title}
                        </Title>
                    )}
                    {subtitle && (
                        <Text
                            fz={isBreakpoint ? 18 : 22}
                            ta={isBreakpoint ? 'left' : 'center'}
                            mt={isBreakpoint ? 10 : 16}
                            c="var(--gm-foreground-fg-secondary)"
                            fw="var(--gm-weight-text)"
                            lh={isBreakpoint ? '140%' : '110%'}
                        >
                            {subtitle}
                        </Text>
                    )}

                    <Link
                        href="https://generalmedicine.co/care/customer-stories/"
                        onClick={() => {
                            trackStoreEvent(StoreAnalytics.TESTIMONIAL, { action: 'see_all_stories' })
                        }}
                    >
                        <Space h={isBreakpoint ? 10 : 16} />
                        <Text
                            ta={isBreakpoint ? 'left' : 'center'}
                            fz={isBreakpoint ? 18 : 22}
                            c="var(--gm-foreground-fg-highlight)"
                            fw="var(--gm-weight-text)"
                            lh="130%"
                        >
                            See success stories
                        </Text>
                    </Link>
                </Flex>
            )}

            <Box
                style={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: '1200px',
                    margin: '0 auto',
                }}
            >
                <AnimatePresence mode="wait" initial={false}>
                    {isBreakpoint ? (
                        <MobileTestimonialCard
                            key={currentTestimonial.id}
                            testimonial={currentTestimonial}
                            onPrev={handlePrev}
                            onNext={handleNext}
                            direction={direction}
                        />
                    ) : (
                        <TestimonialCard
                            key={currentTestimonial.id}
                            testimonial={currentTestimonial}
                            onPrev={handlePrev}
                            onNext={handleNext}
                            showControls={true}
                            direction={direction}
                        />
                    )}
                </AnimatePresence>
            </Box>
        </Box>
    )
}

export default TestimonialCarousel
